import type { CollectionRevisionConnectionRouteIdentity } from '../interfaces/collection-revision-connection.interface';
import type { CollectionRevisionTargetRouteIdentity } from '../collection-flow/collection-revision-route';

function requirePathSegment(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return encodeURIComponent(value.trim());
}

function collectionRoot(apiRoot: string, collectionId: string): string {
  const root = apiRoot.replace(/\/+$/, '');
  return `${root}/collections/${requirePathSegment(
    collectionId,
    'collectionId',
  )}`;
}

export function buildCollectionCurrentWorkflowUrl(
  apiRoot: string,
  collectionId: string,
): string {
  return `${collectionRoot(apiRoot, collectionId)}/workflow`;
}

export function buildCollectionExactWorkflowUrl(
  apiRoot: string,
  collectionId: string,
  revisionId: string,
): string {
  return `${buildCollectionRevisionUrl(
    apiRoot,
    collectionId,
    revisionId,
  )}/workflow`;
}

export function buildCollectionRevisionUrl(
  apiRoot: string,
  collectionId: string,
  revisionId: string,
): string {
  const root = apiRoot.replace(/\/+$/, '');
  return `${root}/collections/${requirePathSegment(
    collectionId,
    'collectionId',
  )}/revisions/${requirePathSegment(revisionId, 'revisionId')}`;
}

export function buildCollectionRevisionStatementOriginsUrl(
  apiRoot: string,
  collectionId: string,
  revisionId: string,
): string {
  return `${buildCollectionRevisionUrl(
    apiRoot,
    collectionId,
    revisionId,
  )}/statement-origins`;
}

export function buildCollectionRevisionStatementOriginUrl(
  apiRoot: string,
  collectionId: string,
  revisionId: string,
  statementOriginId: string,
): string {
  return `${buildCollectionRevisionStatementOriginsUrl(
    apiRoot,
    collectionId,
    revisionId,
  )}/${requirePathSegment(statementOriginId, 'statementOriginId')}`;
}

export function buildCollectionRevisionTargetUrl(
  apiRoot: string,
  collectionId: string,
  revisionId: string,
  targetId: string,
): string {
  return `${buildCollectionRevisionUrl(
    apiRoot,
    collectionId,
    revisionId,
  )}/targets/${requirePathSegment(targetId, 'targetId')}`;
}

export function buildCollectionRevisionTargetConnectionUrl(
  apiRoot: string,
  identity: CollectionRevisionTargetRouteIdentity,
): string {
  return `${buildCollectionRevisionTargetUrl(
    apiRoot,
    identity.collectionId,
    identity.revisionId,
    identity.targetId,
  )}/connection`;
}

export function buildCollectionRevisionTargetConnectionsUrl(
  apiRoot: string,
  identity: CollectionRevisionTargetRouteIdentity,
): string {
  return `${buildCollectionRevisionTargetUrl(
    apiRoot,
    identity.collectionId,
    identity.revisionId,
    identity.targetId,
  )}/connections`;
}

function buildExactConnectionUrl(
  apiRoot: string,
  identity: CollectionRevisionConnectionRouteIdentity,
): string {
  return `${buildCollectionRevisionTargetConnectionsUrl(
    apiRoot,
    identity,
  )}/${requirePathSegment(identity.connectionId, 'connectionId')}`;
}

export function buildCollectionRevisionConnectionCredentialsUrl(
  apiRoot: string,
  identity: CollectionRevisionConnectionRouteIdentity,
): string {
  return `${buildExactConnectionUrl(apiRoot, identity)}/credentials`;
}

export function buildCollectionRevisionConnectionAuthInteractionUrl(
  apiRoot: string,
  identity: CollectionRevisionConnectionRouteIdentity,
): string {
  return `${buildExactConnectionUrl(apiRoot, identity)}/auth-interaction`;
}

export function buildCollectionRevisionConnectionAuthSubmitUrl(
  apiRoot: string,
  identity: CollectionRevisionConnectionRouteIdentity,
): string {
  return `${buildCollectionRevisionConnectionAuthInteractionUrl(
    apiRoot,
    identity,
  )}/submit`;
}

export function buildCollectionRevisionConnectionAuthResendUrl(
  apiRoot: string,
  identity: CollectionRevisionConnectionRouteIdentity,
): string {
  return `${buildCollectionRevisionConnectionAuthInteractionUrl(
    apiRoot,
    identity,
  )}/resend`;
}

export function buildCollectionRevisionConnectionDeleteUrl(
  apiRoot: string,
  identity: CollectionRevisionConnectionRouteIdentity,
): string {
  return buildExactConnectionUrl(apiRoot, identity);
}

export function buildCollectionRevisionTargetEntityWaiverUrl(
  apiRoot: string,
  identity: CollectionRevisionTargetRouteIdentity,
  entityId: string,
): string {
  return `${buildCollectionRevisionTargetEntityUrl(
    apiRoot,
    identity,
    entityId,
  )}/waiver`;
}

function buildCollectionRevisionTargetEntityUrl(
  apiRoot: string,
  identity: CollectionRevisionTargetRouteIdentity,
  entityId: string,
): string {
  return `${buildCollectionRevisionTargetUrl(
    apiRoot,
    identity.collectionId,
    identity.revisionId,
    identity.targetId,
  )}/connection-entities/${requirePathSegment(entityId, 'entityId')}`;
}

export function buildCollectionRevisionTargetEntityWaiverBatchesUrl(
  apiRoot: string,
  identity: CollectionRevisionTargetRouteIdentity,
  entityId: string,
): string {
  return `${buildCollectionRevisionTargetEntityUrl(
    apiRoot,
    identity,
    entityId,
  )}/waiver-batches`;
}

export function buildCollectionRevisionTargetEntityWaiverBatchSignUrl(
  apiRoot: string,
  identity: CollectionRevisionTargetRouteIdentity,
  entityId: string,
  waiverBatchId: string,
): string {
  return `${buildCollectionRevisionTargetEntityWaiverBatchesUrl(
    apiRoot,
    identity,
    entityId,
  )}/${requirePathSegment(waiverBatchId, 'waiverBatchId')}/sign`;
}
